import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { environment } from 'environments/environment';
import { Observable, combineLatest } from 'rxjs';
import { first, startWith } from 'rxjs/operators';
import { UnitOfWorkBase } from '../services';
import { UserService } from '../user/user.service';
import { User } from '../user/user.types';
import { NotificationSink } from './notification-sink';
import { NotificationsUnitOfWork } from './notifications-unit-of-work';

export const BADGE_SINK = new InjectionToken<BadgeSink[]>('BadgeSink');

export abstract class BadgeSink {
  unitOfWork: UnitOfWorkBase;
  navigationService: FuseNavigationService;
  userService: UserService;

  private isStarted = false;

  constructor(
    protected navigationName: string,
    public itemId: string,
    private notificationSink: NotificationSink,
    private methodName: string,
  ) {}

  start(): void {
    if (this.isStarted) {
      return;
    }

    combineLatest([
      this.userService.user$,
      this.notificationSink
        .on$(this.methodName, (observer) => observer.next() /* Update on reconnect */)
        .pipe(startWith(0)),
    ]).subscribe(([user]) => this.updateBadge(user));
    this.isStarted = true;
  }

  private updateBadge(user: User): void {
    this.getTitle(user)
      .pipe(first())
      .subscribe((title) => {
        const navComponent = this.getNavComponent();
        if (!navComponent) {
          return;
        }

        const navigation = navComponent.navigation;
        const item = this.navigationService.getItem(this.itemId, navigation);
        if (!item) {
          return;
        }

        item.badge = {
          ...(item.badge || {}),
          title,
        };
        navComponent.refresh();
      });
  }

  private getNavComponent(): FuseVerticalNavigationComponent {
    return this.navigationService.getComponent<FuseVerticalNavigationComponent>(this.navigationName);
  }

  protected abstract getTitle(user: User): Observable<string>;
}

@Injectable({
  providedIn: 'root',
})
export class BadgeNotificatonService {
  constructor(
    @Inject(BADGE_SINK) @Optional() private badgeSinks: BadgeSink[],
    unitOfWork: NotificationsUnitOfWork,
    navigationService: FuseNavigationService,
    userService: UserService,
  ) {
    this.badgeSinks = badgeSinks || [];
    this.badgeSinks.forEach((badgeSink) => {
      badgeSink.unitOfWork = unitOfWork;
      badgeSink.navigationService = navigationService;
      badgeSink.userService = userService;
    });
  }

  start(): void {
    const initTaskBadge = !!environment.includeSandboxRoute;
    this.badgeSinks
      .filter((badgeSink) => badgeSink.itemId !== 'tasks' || initTaskBadge)
      .forEach((badgeSink) => badgeSink.start());
  }
}
